import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { App, Button, Select, Space, Typography, Tag, Alert } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { domainsCacheService, type Domain } from "@/services/domainsCache";

const { Text } = Typography;
const { Option } = Select;

export const DomainSelector: React.FC<{
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}> = ({ value, onChange, placeholder, disabled }) => {
  const { message } = App.useApp();
  const { t } = useTranslation();
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshCooldown, setRefreshCooldown] = useState(0);
  const isDisabled = !!disabled;

  useEffect(() => {
    const unsubscribe = domainsCacheService.subscribe((cache) => {
      setDomains(cache.domains);
      setLoading(cache.isLoading);
      setError(cache.error);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const remaining = domainsCacheService.getRefreshCooldownRemaining();
      setRefreshCooldown(Math.ceil(remaining / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = async () => {
    if (isDisabled) return;
    try {
      await domainsCacheService.refresh();
      message.success(t("apps.domainsUpdate"));
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : t("apps.domainsError")
      );
    }
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <Text>
          {t("apps.domain")} ({domains.length} доступно)
        </Text>
        <Button
          size="small"
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
          loading={loading}
          disabled={isDisabled || loading || refreshCooldown > 0}
        >
          {loading
            ? t("common.loading")
            : refreshCooldown > 0
              ? `${refreshCooldown}с`
              : t("apps.domainsRefresh")}
        </Button>
      </div>
      <Select
        value={value}
        onChange={onChange}
        placeholder={loading ? t("apps.domainsLoading") : placeholder}
        loading={loading}
        disabled={isDisabled || loading}
        showSearch
        filterOption={(input, option) => {
          const searchText = input.toLowerCase();
          const domainName = option?.label?.toString().toLowerCase() || "";
          return domainName.includes(searchText);
        }}
        style={{ width: "100%" }}
        optionLabelProp="label"
      >
        {domains.map((domain) => (
          <Option key={domain.id} value={domain.name} label={domain.name}>
            <Space>
              <span>{domain.name}</span>
              <Tag color={domain.status === "active" ? "green" : "orange"}>
                {domain.status}
              </Tag>
            </Space>
          </Option>
        ))}
      </Select>
      {error && (
        <Alert
          message={t("apps.domainsError")}
          description={error}
          type="error"
          showIcon
          style={{ marginTop: 8 }}
        />
      )}
    </div>
  );
};
